import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import usageLeagueApi from "../../../api/analytics/usageLeagueApi";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { AnalyticsWidgetUiDetailsDto } from "../../../types/analytics";
import {
  UsageLeagueDimension,
  UsageLeagueDto,
  UsageLeagueRowDto,
} from "../../../types/analytics/UsageLeagueDto";
import PaginationControls from "../../common/PaginationControls";
import UserContext from "../../../state/UserContext";
import UsageCompletionBar from "./UsageCompletionBar";
import { usageLeagueDimensions } from "../../../config/analytics/usageLeagueDimensions";
import { getCompletedSegmentColor } from "../../../helpers/usage/usageStatusChartHelpers";
import { UsageStatusDatasetItem } from "../../../types/analytics/UsageStatusDataset";

type LeagueState = "LOADING" | "READY" | "ERROR";

const defaultPageSize = 10;
const paginationThreshold = 15;

interface UsageLeagueTableProps {
  filters: AnalyticsFilterInputs;
  widget: AnalyticsWidgetUiDetailsDto;
}

function UsageLeagueTable({ filters, widget }: UsageLeagueTableProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const clientColour = userContext.user.client.primaryHexColour;
  const themeColor = clientColour ? `#${clientColour}` : undefined;
  const api = useMemo(
    () => new usageLeagueApi(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const [state, setState] = useState<LeagueState>("LOADING");
  const [dimension, setDimension] =
    useState<UsageLeagueDimension>("LOCATION");
  const [league, setLeague] = useState<UsageLeagueDto | null>(null);
  const [activeParentLocationId, setActiveParentLocationId] = useState<
    number | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const requestGenerationRef = useRef(0);
  const completedColor = getCompletedSegmentColor(
    (widget.datasets?.[0]?.items ?? []) as UsageStatusDatasetItem[],
  );
  const activeDimension =
    usageLeagueDimensions.find((item) => item.key === dimension) ??
    usageLeagueDimensions[0];

  const loadLeague = useCallback(
    async (
      nextDimension: UsageLeagueDimension,
      parentLocationId?: number,
    ) => {
      const requestGeneration = ++requestGenerationRef.current;
      setActiveParentLocationId(parentLocationId);
      setState("LOADING");
      const result = await api.getLeague(
        filters,
        nextDimension,
        parentLocationId,
      );
      if (requestGeneration !== requestGenerationRef.current) return;

      if (result.status === "OK") {
        setLeague(result.league);
        setCurrentPage(1);
        setState("READY");
        return;
      }

      setState("ERROR");
    },
    [api, filters],
  );

  useEffect(() => {
    setDimension("LOCATION");
    setLeague(null);
    setActiveParentLocationId(undefined);
    setCurrentPage(1);
    setPageSize(defaultPageSize);
    loadLeague("LOCATION");
  }, [loadLeague]);

  const selectDimension = (nextDimension: UsageLeagueDimension) => {
    setDimension(nextDimension);
    setLeague(null);
    setActiveParentLocationId(undefined);
    setCurrentPage(1);

    loadLeague(nextDimension);
  };

  const rows = league?.rows ?? [];
  const safePageSize = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(rows.length / safePageSize));
  const safePage = Math.min(currentPage, pageCount);
  const visibleRows = rows.slice(
    (safePage - 1) * safePageSize,
    safePage * safePageSize,
  );

  return (
    <section
      className="mx-3 mt-8 border-t border-gray-200 pt-5"
      aria-labelledby="usage-league-heading"
      aria-busy={state === "LOADING"}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2
            id="usage-league-heading"
            className="font-heading text-xl font-semibold text-gray-700"
          >
            {t("Analytics.Widgets.Usage.League.Heading", "League table")}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            {league && state === "READY"
              ? t("Analytics.Widgets.Usage.League.Benchmark", {
                  defaultValue:
                    "Benchmark is the current selection's average ({{percent}}%).",
                  percent: league.benchmarkPercent,
                })
              : t(
                  "Analytics.Widgets.Usage.League.Description",
                  "Compare completion rates across locations or job levels.",
                )}
          </p>
        </div>

        <div className="shrink-0">
          <label className="flex items-center gap-2 sm:hidden">
            <span className="text-sm font-semibold text-gray-700">
              {t("Analytics.Widgets.Usage.League.RankBy", "Rank by")}
            </span>
            <select
              value={dimension}
              disabled={state === "LOADING"}
              onChange={(event) =>
                selectDimension(event.target.value as UsageLeagueDimension)
              }
              className="rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
            >
              {usageLeagueDimensions.map((item) => (
                <option key={item.key} value={item.key}>
                  {t(item.label.translationKey, item.label.defaultValue)}
                </option>
              ))}
            </select>
          </label>

          <div className="hidden sm:block">
            <span className="mb-1.5 block text-sm font-semibold text-gray-700">
              {t("Analytics.Widgets.Usage.League.RankBy", "Rank by")}
            </span>
            <div
              role="group"
              aria-label={t(
                "Analytics.Widgets.Usage.League.RankBy",
                "Rank by",
              )}
              className="flex max-w-full overflow-x-auto rounded-md shadow-sm"
            >
              {usageLeagueDimensions.map((item, index) => {
                const isSelected = dimension === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={state === "LOADING"}
                    aria-pressed={isSelected}
                    onClick={() => selectDimension(item.key)}
                    className={cx(
                      "whitespace-nowrap border px-3 py-2 text-sm transition-colors first:rounded-l-md last:rounded-r-md focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]",
                      index > 0 && "-ml-px",
                      isSelected
                        ? "relative z-10 border-gray-500 bg-gray-100 font-semibold text-gray-900"
                        : "border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50",
                    )}
                    style={
                      isSelected && themeColor
                        ? {
                            backgroundColor: `${themeColor}1a`,
                            borderColor: themeColor,
                            color: themeColor,
                          }
                        : undefined
                    }
                  >
                    {t(item.label.translationKey, item.label.defaultValue)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {state === "LOADING" && (
        <>
          <span className="sr-only" role="status">
            {t(
              "Analytics.Widgets.Usage.League.Loading",
              "Loading league table.",
            )}
          </span>
          <LeagueSkeleton />
        </>
      )}

      {state === "ERROR" && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 px-4 py-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-700">
            {t(
              "Analytics.Widgets.Usage.League.Error",
              "The league table could not be loaded. The rest of the page is unaffected.",
            )}
          </p>
          <button
            type="button"
            className="btn-secondary btn-small shrink-0 self-start sm:self-auto"
            onClick={() =>
              loadLeague(dimension, activeParentLocationId)
            }
          >
            {t("Analytics.Widgets.Usage.League.Retry", "Try again")}
          </button>
        </div>
      )}

      {state === "READY" && league && (
        <>
          {activeDimension.hierarchical && (
            <LeagueBreadcrumb
              league={league}
              onNavigate={(parentLocationId) =>
                loadLeague(dimension, parentLocationId)
              }
            />
          )}

          {rows.length === 0 ? (
            <p className="mt-4 rounded-lg border border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
              {t(
                "Analytics.Widgets.Usage.League.Empty",
                "There are no assigned journeys to rank for this dimension and selection.",
              )}
            </p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
              <div className="hidden grid-cols-[3rem_minmax(10rem,1.2fr)_minmax(14rem,2fr)_7rem] gap-4 bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-600 md:grid">
                <span>
                  {t("Analytics.Widgets.Usage.League.Rank", "Rank")}
                </span>
                <span>
                  {t("Analytics.Widgets.Usage.League.Label", "Name")}
                </span>
                <span>
                  {t(
                    "Analytics.Widgets.Usage.League.Completion",
                    "Completion",
                  )}
                </span>
                <span className="text-right">
                  {t("Analytics.Widgets.Usage.League.Assigned", "Assigned")}
                </span>
              </div>
              <ol
                className="divide-y divide-gray-200 bg-white"
                start={(safePage - 1) * safePageSize + 1}
              >
                {visibleRows.map((row, index) => (
                  <LeagueRow
                    key={`${row.id ?? "unassigned"}-${row.label}`}
                    row={row}
                    rank={(safePage - 1) * safePageSize + index + 1}
                    benchmarkPercent={league.benchmarkPercent}
                    completedColor={completedColor}
                    hierarchical={activeDimension.hierarchical}
                    onDrill={
                      row.id !== null && row.hasChildren
                        ? () => loadLeague(dimension, row.id!)
                        : undefined
                    }
                  />
                ))}
              </ol>
            </div>
          )}

          {rows.length > paginationThreshold && (
            <PaginationControls
              activePage={safePage}
              itemsPerPage={safePageSize}
              totalItemCount={rows.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

function LeagueBreadcrumb({
  league,
  onNavigate,
}: {
  league: UsageLeagueDto;
  onNavigate: (parentLocationId?: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <nav
      className="mt-4 flex flex-wrap items-center gap-2 text-sm"
      aria-label={t(
        "Analytics.Widgets.Usage.League.LocationBreadcrumb",
        "League table location",
      )}
    >
      <button
        type="button"
        className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
        aria-current={league.breadcrumb.length === 0 ? "page" : undefined}
        onClick={() => onNavigate()}
      >
        {t(
          "Analytics.Widgets.Usage.League.AllLocations",
          "All locations",
        )}
      </button>
      {league.breadcrumb.map((node, index) => {
        const isCurrent = index === league.breadcrumb.length - 1;
        return (
          <span key={node.id} className="flex items-center gap-2">
            <span aria-hidden className="text-gray-400">
              /
            </span>
            {isCurrent ? (
              <span className="font-semibold text-gray-700" aria-current="page">
                {node.label}
              </span>
            ) : (
              <button
                type="button"
                className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                onClick={() => onNavigate(node.id)}
              >
                {node.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/** Podium places read as gold, silver, and bronze; everyone else stays a neutral white chip. */
function getRankChipClassNames(rank: number) {
  switch (rank) {
    case 1:
      return "border-amber-400 bg-amber-200 text-amber-900";
    case 2:
      return "border-gray-400 bg-gray-200 text-gray-700";
    case 3:
      return "border-orange-400 bg-orange-200 text-orange-900";
    default:
      return "border-gray-200 bg-white text-gray-600 shadow-sm";
  }
}

function LeagueRow({
  row,
  rank,
  benchmarkPercent,
  completedColor,
  hierarchical,
  onDrill,
}: {
  row: UsageLeagueRowDto;
  rank: number;
  benchmarkPercent: number;
  completedColor: string;
  hierarchical: boolean;
  onDrill?: () => void;
}) {
  const { t } = useTranslation();
  const label = row.translateLabel ? t(row.label, row.label) : row.label;

  return (
    <li className="px-4 py-3.5">
      <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-3 md:grid-cols-[3rem_minmax(10rem,1.2fr)_minmax(14rem,2fr)_7rem] md:gap-4">
        <span
          className={cx(
            "inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold tabular-nums",
            getRankChipClassNames(rank),
          )}
        >
          {rank}
        </span>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {hierarchical && onDrill ? (
              <button
                type="button"
                onClick={onDrill}
                aria-label={t(
                  "Analytics.Widgets.Usage.League.DrillInto",
                  "View locations in {{name}}",
                  { name: label },
                )}
                className="min-w-0 truncate font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              >
                {label}
                <FontAwesomeIcon
                  icon={faChevronRight}
                  size="xs"
                  className="ml-1.5"
                  aria-hidden
                />
              </button>
            ) : (
              <span className="min-w-0 truncate font-semibold text-gray-800">
                {label}
              </span>
            )}
            {row.smallTeam && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                {t(
                  "Analytics.Widgets.Usage.League.SmallTeam",
                  "Small team",
                )}
              </span>
            )}
            <span className="text-xs text-gray-500 md:hidden">
              {t("Analytics.Widgets.Usage.League.AssignedInline", {
                defaultValue: "{{formattedCount}} assigned",
                formattedCount: row.assignedCount.toLocaleString(),
              })}
            </span>
          </div>
        </div>

        <UsageCompletionBar
          percent={row.completionPercent}
          fillColor={
            row.completionPercent >= benchmarkPercent
              ? completedColor
              : undefined
          }
          fillClassName={
            row.completionPercent < benchmarkPercent
              ? "bg-amber-300"
              : undefined
          }
          benchmarkPercent={benchmarkPercent}
          className="col-span-2 w-full md:col-span-1"
          barClassName="h-3 w-full"
        />

        <span className="hidden text-right text-sm tabular-nums text-gray-500 md:block">
          {row.assignedCount.toLocaleString()}
        </span>
      </div>
    </li>
  );
}

function LeagueSkeleton() {
  return (
    <div
      className="mt-4 overflow-hidden rounded-lg border border-gray-200"
      aria-hidden
    >
      {[0, 1, 2, 3, 4].map((row) => (
        <div
          key={row}
          className="grid animate-pulse grid-cols-[2.5rem_1fr] gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 md:grid-cols-[3rem_1.2fr_2fr_7rem] md:gap-4"
        >
          <div className="h-7 w-7 rounded-full bg-gray-200" />
          <div className="h-4 w-2/3 rounded bg-gray-200" />
          <div className="col-span-2 h-3 rounded-full bg-gray-100 md:col-span-1" />
          <div className="hidden h-4 w-12 justify-self-end rounded bg-gray-100 md:block" />
        </div>
      ))}
    </div>
  );
}

export default UsageLeagueTable;
